/**
 * NotepadFullView — a note opened "like a word doc" in the editor's center
 * stage while the script swaps into the right panel, read-only.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Minimize2 } from "lucide-react";
import { NoteDocEditor } from "@/components/editor/NotepadDoc";

export function NotepadFullView({ scriptId, noteId, canModerate, currentUserId, onExit }) {
  const [note, setNote] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/scripts/${scriptId}/notepad`)
      .then(({ data }) => {
        if (!alive) return;
        const n = (data.notes || []).find((x) => x.id === noteId);
        if (n) setNote(n); else setMissing(true);
      })
      .catch(() => alive && setMissing(true));
    return () => { alive = false; };
  }, [scriptId, noteId]);

  return (
    <div className="max-w-[8.5in] mx-auto" data-testid="notepad-full-view">
      <div className="flex items-center gap-3 mb-3">
        <button
          onClick={onExit}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-[var(--line)] bg-[var(--paper)] hover:border-[var(--brand)]/60 hover:bg-[var(--warm-accent)] transition-colors"
          data-testid="notepad-exit-full-btn"
        >
          <Minimize2 size={13} strokeWidth={1.75} /> Back to script
        </button>
        <span className="text-[11px] text-[var(--ink-muted)] font-sans hidden sm:block">
          Your script is on the right, read-only, while you write.
        </span>
      </div>

      {missing ? (
        <div className="bg-white border border-[var(--line)] rounded-sm shadow-xl p-10 text-center">
          <p className="text-sm text-[var(--ink-muted)]">Couldn't open this note — it may have been deleted.</p>
        </div>
      ) : !note ? (
        <div className="bg-white border border-[var(--line)] rounded-sm shadow-xl p-10 text-center">
          <p className="text-sm text-[var(--ink-muted)]">Opening the note…</p>
        </div>
      ) : (
        <div className="bg-white border border-[var(--line)] rounded-sm shadow-xl" style={{ minHeight: "11in" }}>
          <NoteDocEditor
            key={note.id}
            scriptId={scriptId}
            note={note}
            canEdit={canModerate || note.author_id === currentUserId}
            page
            onSaved={setNote}
          />
        </div>
      )}
    </div>
  );
}
